import type { ReactNode } from 'react'
import type { LumpsumRebalanceCategory } from '../../../api/rebalance.types'
import { PlanCategoryBucketKey, type PlansActionFilter } from '../../../api/types'
import { CategoryCard } from '../../lumpsum-ui-components/CategoryCard'
import { RebalanceBuyBucketAccordion } from './buckets/RebalanceBuyBucketAccordion'
import { RebalanceExecutedBucketAccordion } from './buckets/RebalanceExecutedBucketAccordion'
import { RebalanceInvalidatedBucketAccordion } from './buckets/RebalanceInvalidatedBucketAccordion'
import { RebalanceNoActionBucketAccordion } from './buckets/RebalanceNoActionBucketAccordion'
import { RebalanceSellBucketAccordion } from './buckets/RebalanceSellBucketAccordion'
import { RebalanceUnderReviewBucketAccordion } from './buckets/RebalanceUnderReviewBucketAccordion'
import { getVisiblePlanCategoryBucketKeys, resolvePlanCategoryKey } from '../../../../utils/lumpsum/planListUtils'

type RebalanceCategoryCardProps = {
  category: LumpsumRebalanceCategory
  categoryIndex: number
  actionFilter: PlansActionFilter
  noDivider?: boolean
}

type RebalanceBucketAccordionProps = {
  category: LumpsumRebalanceCategory
  categoryId: string
  actionFilter: PlansActionFilter
  isBorderTop?: boolean
}

export function RebalanceCategoryCard({
  category,
  categoryIndex,
  actionFilter,
  noDivider = false
}: RebalanceCategoryCardProps) {
  const categoryKey = resolvePlanCategoryKey(category, categoryIndex)
  const label = category?.categoryName ?? categoryKey.replace(/_/g, ' ')
  const sharedBucketProps = {
    category,
    categoryId: categoryKey,
    actionFilter
  }

  return (
    <CategoryCard label={label} noDivider={noDivider}>
      {getVisiblePlanCategoryBucketKeys(category, actionFilter).map((bucketKey, bucketIndex) =>
        renderRebalancePlanCategoryBucket(bucketKey, {
          ...sharedBucketProps,
          isBorderTop: bucketIndex > 0
        })
      )}
    </CategoryCard>
  )
}

function renderRebalancePlanCategoryBucket(
  bucketKey: PlanCategoryBucketKey,
  bucketProps: RebalanceBucketAccordionProps
): ReactNode {
  if (bucketKey === PlanCategoryBucketKey.sellRecommendations) {
    return <RebalanceSellBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.buyRecommendations) {
    return <RebalanceBuyBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.underReviewRecommendations) {
    return <RebalanceUnderReviewBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.executedRecommendations) {
    return <RebalanceExecutedBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.invalidatedRecommendations) {
    return <RebalanceInvalidatedBucketAccordion key={bucketKey} {...bucketProps} />
  }
  if (bucketKey === PlanCategoryBucketKey.noActionFunds) {
    return <RebalanceNoActionBucketAccordion key={bucketKey} {...bucketProps} />
  }
  return null
}
